/**
 * OffersTab — every offer that's touched this property.
 *
 * Reads the property timeline and filters to OFFER_* codes. Each row
 * summarises price/status/timestamp with a link into the Offer
 * Intelligence engine for full authoring.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Loader2, FileSignature, ArrowRight, CheckCircle2, XCircle, Send, Clock,
} from "lucide-react";
import StrategyComparisonCard from "@/components/v3/StrategyComparisonCard";

const OFFER_CODES = new Set([
  "offer_created", "offer_sent", "offer_accepted", "offer_rejected",
  "offer_submitted", "offer_countered", "offer_presented",
  "counteroffer_received",
]);

const STATUS_STYLE = {
  offer_created:         { label: "Drafted",   icon: FileSignature, color: "#94A3B8" },
  offer_sent:            { label: "Sent",      icon: Send,          color: "#F59E0B" },
  offer_submitted:       { label: "Submitted", icon: Send,          color: "#F59E0B" },
  offer_presented:       { label: "Presented", icon: Send,          color: "#F59E0B" },
  offer_countered:       { label: "Countered", icon: Clock,         color: "#8B5CF6" },
  counteroffer_received: { label: "Counter",   icon: Clock,         color: "#8B5CF6" },
  offer_accepted:        { label: "Accepted",  icon: CheckCircle2,  color: "#10B981" },
  offer_rejected:        { label: "Rejected",  icon: XCircle,       color: "#DC2626" },
};

const money = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

export default function OffersTab({ property }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/v3/properties/${property.id}/timeline?limit=200`,
      );
      const filtered = (data.items || []).filter(
        (ev) => OFFER_CODES.has(String(ev.event_code || "").toLowerCase()),
      );
      setEvents(filtered);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [property.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="tab-panel-offers">
      {/* Deal Strategy Engine — all 8 strategies side-by-side */}
      <StrategyComparisonCard property={property} />

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <FileSignature className="w-3.5 h-3.5" /> Offer History
          </div>
          <Link
            to={`/v3/intel/offer-intel?property_id=${property.id}`}
            data-testid="offers-intel-link"
            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
          >
            Offer Intelligence <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {loading ? (
          <div className="p-6 flex items-center gap-2 text-slate-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading offers…
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center" data-testid="offers-empty">
            <FileSignature className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <div className="text-slate-900 font-semibold text-sm">No offers logged yet</div>
            <div className="text-[12px] text-slate-500 mt-1 max-w-sm mx-auto">
              Draft your first offer in the <b>Offer Intelligence</b> engine.
              Every offer sent, countered, or accepted lands here.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map((ev, i) => {
              const style = STATUS_STYLE[String(ev.event_code || "").toLowerCase()] || STATUS_STYLE.offer_created;
              const Icon = style.icon;
              const amount = money(ev.payload?.amount ?? ev.payload?.offer_amount);
              return (
                <li key={ev.id || i}
                    data-testid={`offer-event-${i}`}
                    className="px-4 py-3 flex items-center gap-3">
                  <span
                    className="rounded-md p-1.5 shrink-0"
                    style={{ backgroundColor: `${style.color}15`, borderColor: `${style.color}30` }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: style.color }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-900">
                      {style.label}
                      {amount && <span className="ml-2 text-slate-600 tabular-nums font-bold">{amount}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {ev.created_at?.replace("T", " ").slice(0, 16) || "—"}
                    </div>
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-widest font-black shrink-0"
                    style={{ color: style.color }}
                  >{style.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
